import { useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Box } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  setStatus,
} from '../../../store/walletTransaction/walletTransactionSlice';
import Loader from '../../../components/Loader';
import { useFormik } from 'formik';
import Scrollbar from '../../../components/Scrollbar';
import { omit } from 'lodash';
import { setStatus as setWalletTransactionStatus } from '../../../store/walletTransaction/walletTransactionSlice';
import {
  findWalletTransaction,
  createWalletTransaction,
  patchWalletTransaction,
} from '../../../store/walletTransaction/api';
import walletTransactionSchema from '../../../validation/walletTransactionSchema';
import { Users } from '../../../store/users/api/interfaces';
import WalletTransactionFormModal from './WalletTransactionFormModal';
import { WALLET_PAYMENT_METHOD } from '../../../constants';

interface IProps {
  user: Users | undefined;
  toggle: boolean;
  onOpen: () => void;
  onClose: () => void;
  onBalanceUpdated?: (balance: number) => void;
}

const Index: React.FC<IProps> = ({
  user,
  toggle,
  onClose,
  onBalanceUpdated,
}) => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.walletTransaction);
  const currentBalance = Number(user?.balance || 0);

  useEffect(() => {
    if (user?.id) {
      dispatch(
        findWalletTransaction({
          limit,
          skip,
          userId: user.id,
        })
      );
    }
  }, [dispatch, limit, skip, user]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
  } = useFormik({
    initialValues: {
      id: '',
      amount: '',
      userId: user?.id ?? '',
      paymentMethod: 'creditNote',
      cdType: 'C' as 'C' | 'D',
      currentBalance,
    },
    enableReinitialize: true,
    validationSchema: walletTransactionSchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await dispatch(patchWalletTransaction(values)).unwrap();
        }
        if (!values.id) {
          const reqBody = {
            ...omit(values, ['id', 'currentBalance']),
            cdType: (values.paymentMethod === 'reduceNote' ? 'D' : 'C') as
              | 'C'
              | 'D',
          };
          const result: any = await dispatch(
            createWalletTransaction(reqBody)
          ).unwrap();
          if (
            onBalanceUpdated &&
            result?.updatedBalance !== undefined &&
            result?.updatedBalance !== null
          ) {
            onBalanceUpdated(Number(result.updatedBalance));
          }
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setWalletTransactionStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  useEffect(() => {
    setFieldValue('userId', user?.id);
    setFieldValue('currentBalance', currentBalance);
  }, [user?.id, currentBalance, setFieldValue]);

  return (
    <>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 1000, position: 'relative', pt: 3 }}>
          <Table size={'medium'}>
            <TableHead>
              <TableRow>
                <TableCell align='center'>{'Amount'}</TableCell>
                <TableCell align='center'>{'Payment Method'}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow hover>
                  <TableCell
                    colSpan={2}
                    align='center'
                  >
                    <Loader />
                  </TableCell>
                </TableRow>
              ) : data?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    align='center'
                  >
                    {'No record found'}
                  </TableCell>
                </TableRow>
              ) : (
                data?.map((walletTransaction) => (
                  <TableRow key={walletTransaction.id}>
                    <TableCell align='center'>
                      {walletTransaction?.amount}
                    </TableCell>
                    <TableCell align='center'>
                      {WALLET_PAYMENT_METHOD.find(
                        (method) =>
                          method.value === walletTransaction?.paymentMethod
                      )?.name || walletTransaction?.paymentMethod}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component='div'
          count={totalRecords}
          rowsPerPage={limit}
          page={Math.min(Math.round(totalRecords / limit), currentPage)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>

      <WalletTransactionFormModal
        touched={touched}
        errors={errors}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        onClose={onClose}
        resetForm={resetForm}
        setFieldValue={setFieldValue}
        toggle={toggle}
        values={values}
        currentBalance={currentBalance}
      />
    </>
  );
};

export default Index;
